import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Controller } from "react-hook-form";

export default function MeterFormDialog({
  open,
  onClose,
  isCreateMode,
  control,
  handleSubmit,
  onSubmitForm,
  isSubmitting,
  isDirty,
  loading,
  meterTypes,
  unitNatives,
  galConversions,
}) {
  return (
    <Dialog onClose={onClose} open={open} maxWidth="sm" fullWidth>
      <DialogTitle>{isCreateMode ? "Add Meter" : "Edit Meter"}</DialogTitle>
      <DialogContent>
        {loading ? (
          <CircularProgress />
        ) : (
          <Box
            component="form"
            id="meter-form"
            onSubmit={handleSubmit(onSubmitForm)}
          >
            <Controller
              name="meter_serial"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  autoFocus
                  margin="normal"
                  label="Serial Number *"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="meter_id"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  margin="normal"
                  label="Meter ID *"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="meter_make"
              control={control}
              render={({ field }) => (
                <TextField {...field} margin="normal" label="Make" fullWidth />
              )}
            />

            <Controller
              name="meter_model"
              control={control}
              render={({ field }) => (
                <TextField {...field} margin="normal" label="Model" fullWidth />
              )}
            />

            <Controller
              name="meter_type_ndx"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  margin="normal"
                  error={!!fieldState.error}
                >
                  <InputLabel>Type *</InputLabel>
                  <Select {...field} value={field.value || ""} label="Type *">
                    {meterTypes?.map((type) => (
                      <MenuItem
                        key={type.meter_type_ndx}
                        value={type.meter_type_ndx}
                      >
                        {type.meter_type_label}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldState.error && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 2 }}
                    >
                      {fieldState.error.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="unit_native_ndx"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  margin="normal"
                  error={!!fieldState.error}
                >
                  <InputLabel>Unit Native *</InputLabel>
                  <Select
                    {...field}
                    value={field.value || ""}
                    label="Unit Native *"
                  >
                    {unitNatives?.map((unit) => (
                      <MenuItem
                        key={unit.unit_native_ndx}
                        value={unit.unit_native_ndx}
                      >
                        {unit.unit_native_label}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldState.error && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 2 }}
                    >
                      {fieldState.error.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="gal_conversion_ndx"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  margin="normal"
                  error={!!fieldState.error}
                >
                  <InputLabel>Gallon Conversion *</InputLabel>
                  <Select
                    {...field}
                    value={field.value || ""}
                    label="Gallon Conversion *"
                  >
                    {galConversions?.map((conversion) => (
                      <MenuItem
                        key={conversion.gal_conversion_ndx}
                        value={conversion.gal_conversion_ndx}
                      >
                        {conversion.gal_conversion_label}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldState.error && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 2 }}
                    >
                      {fieldState.error.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="rollover_num"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  label="Rollover Number"
                  fullWidth
                />
              )}
            />

            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  label="Notes"
                  multiline
                  rows={3}
                  fullWidth
                />
              )}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={isSubmitting || loading} onClick={onClose}>
          Cancel
        </Button>
        <Button
          sx={{ width: 100 }}
          form="meter-form"
          type="submit"
          variant="contained"
          color="secondary"
          disabled={isSubmitting || loading || !isDirty}
        >
          {isSubmitting ? (
            <CircularProgress color="#fff" size={24} />
          ) : isCreateMode ? (
            "Create"
          ) : (
            "Update"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
